import { motion } from 'framer-motion'
import { useState } from 'react'
import { sacredTexts } from '@/data/frameworks'
import { cn } from '@/lib/utils'
import { BookOpen, Quote, List } from 'lucide-react'

export default function Step4() {
  const [selectedText, setSelectedText] = useState<string | null>(sacredTexts[0].id)

  const activeText = sacredTexts.find(t => t.id === selectedText)

  return (
    <div className="space-y-12">
      {/* Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-3xl mx-auto"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 mb-6 shadow-divine">
          <span className="text-4xl">📜</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-gold-gradient mb-6">
          Sacred Scriptures
        </h1>
        <p className="text-lg text-gold-700/80 dark:text-gold-300/80 leading-relaxed">
          The foundational texts that have guided humanity's spiritual journey for millennia.
          Each scripture offers a unique window into divine truth and eternal wisdom.
        </p>
      </motion.div>

      {/* Text Selector - Visual Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {sacredTexts.map((text, index) => (
          <motion.button
            key={text.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => setSelectedText(text.id)}
            className={cn(
              'p-6 rounded-xl transition-all duration-300 text-center',
              selectedText === text.id
                ? 'bg-gold-500 text-white shadow-divine scale-105'
                : 'sacred-card hover:scale-105'
            )}
          >
            <span className="text-4xl block mb-2">{text.symbol}</span>
            <span className={cn(
              'font-display text-sm font-medium',
              selectedText === text.id
                ? 'text-white'
                : 'text-gold-700 dark:text-gold-300'
            )}>
              {text.title.split(' ').slice(1).join(' ') || text.title}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Selected Scripture Details */}
      {activeText && (
        <motion.div
          key={activeText.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sacred-card p-8 divine-glow"
        >
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left: Icon and Title */}
            <div className="lg:w-1/3 text-center">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-2xl bg-gradient-to-br from-gold-100 to-gold-200 dark:from-gold-900/30 dark:to-gold-800/30 mb-4">
                <span className="text-7xl">{activeText.symbol}</span>
              </div>
              <h2 className="font-display text-3xl font-bold text-gold-800 dark:text-gold-200 mb-2">
                {activeText.title}
              </h2>
              <p className="text-gold-600 dark:text-gold-400">
                {activeText.tradition}
              </p>
            </div>

            {/* Right: Details */}
            <div className="lg:w-2/3 space-y-6">
              {/* Description */}
              <div className="flex items-start gap-3">
                <BookOpen className="w-5 h-5 text-gold-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-display font-semibold text-gold-800 dark:text-gold-200 mb-2">
                    About
                  </h4>
                  <p className="text-gold-700/70 dark:text-gold-300/70">
                    {activeText.description}
                  </p>
                </div>
              </div>

              {/* Key Teachings */}
              <div className="flex items-start gap-3">
                <List className="w-5 h-5 text-gold-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-display font-semibold text-gold-800 dark:text-gold-200 mb-3">
                    Key Teachings
                  </h4>
                  <ul className="space-y-3">
                    {activeText.keyTeachings.map((teaching, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-2"
                      >
                        <span className="w-6 h-6 rounded-full bg-gold-100 dark:bg-gold-900/30 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gold-600">
                          {index + 1}
                        </span>
                        <span className="text-gold-700/70 dark:text-gold-300/70">
                          {teaching}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Comparative View */}
      <div className="space-y-6">
        <h3 className="font-display text-2xl font-semibold text-center text-gold-800 dark:text-gold-200">
          Scriptures of the World
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-gold-200 dark:border-gold-800">
                <th className="text-left py-4 px-4 font-display font-semibold text-gold-800 dark:text-gold-200">
                  Scripture
                </th>
                <th className="text-left py-4 px-4 font-display font-semibold text-gold-800 dark:text-gold-200">
                  Tradition
                </th>
                <th className="text-left py-4 px-4 font-display font-semibold text-gold-800 dark:text-gold-200">
                  Central Theme
                </th>
              </tr>
            </thead>
            <tbody>
              {sacredTexts.map((text, index) => (
                <motion.tr
                  key={text.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  onClick={() => setSelectedText(text.id)}
                  className={cn(
                    'border-b border-gold-100 dark:border-gold-900/30 cursor-pointer transition-colors',
                    selectedText === text.id
                      ? 'bg-gold-100 dark:bg-gold-900/30'
                      : 'hover:bg-gold-50 dark:hover:bg-gold-900/20'
                  )}
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{text.symbol}</span>
                      <span className="font-medium text-gold-800 dark:text-gold-200">
                        {text.title}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-gold-600/70 dark:text-gold-400/70">
                    {text.tradition}
                  </td>
                  <td className="py-4 px-4 text-gold-600/70 dark:text-gold-400/70">
                    {text.keyTeachings[0]}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Inspirational Quote */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="sacred-card p-8 text-center max-w-2xl mx-auto"
      >
        <Quote className="w-8 h-8 text-gold-400 mx-auto mb-4" />
        <blockquote className="font-serif text-xl italic text-gold-800 dark:text-gold-200 mb-4">
          "Sacred scriptures are like different lamps illuminating the same truth.
          Though the vessels differ, the light they carry is one."
        </blockquote>
        <footer className="text-gold-600 dark:text-gold-400 text-sm">
          — Ancient Wisdom
        </footer>
      </motion.div>
    </div>
  )
}
